import { useCallback, useEffect, useSyncExternalStore } from 'react';
import api from './api';
import {getPreview} from './employeePreview';

const stores = new Map();
const sessions = new WeakMap();
let sessionId = 0;
export function interestCacheKey(user) {
  if (user && !sessions.has(user)) sessions.set(user, ++sessionId);
  return JSON.stringify([user ? sessions.get(user) : 0, getPreview()?.preview_token || 'live',
    user?.id, user?.role, user?.base_role, user?.team_id, user?.areas, user?.allowed_areas]);
}
export const interestEndpoint = user => user?.role === 'promoter' ? '/promoter/interesting-contacts' : '/interesting-contacts';
function entryFor(user) {
  const key = interestCacheKey(user);
  if (!stores.has(key)) stores.set(key, {listeners:new Set(), request:null, version:0,
    state:{ids:new Set(), pending:new Set(), ready:false, error:null}});
  return stores.get(key);
}
export const interestRevision = user => entryFor(user).version;
function update(entry, values) {
  entry.state = {...entry.state, ...values};
  entry.listeners.forEach(listener => listener());
}
export async function loadInterests(user, retry = false) {
  if (!user?.id) return;
  const entry = entryFor(user);
  if (entry.request) return entry.request;
  if (!retry && (entry.state.error || (entry.state.ready && Date.now() - (entry.loadedAt || 0) < 30000))) return;
  const version = entry.version;
  update(entry, {error:null});
  entry.request = Promise.resolve().then(() => api.get(`${interestEndpoint(user)}/ids`)).then(({data}) => {
    if (version === entry.version) {entry.loadedAt=Date.now();update(entry, {ids:new Set(data.ids || []), ready:true});}
  }).catch(error => {if(version === entry.version) update(entry, {error});}).finally(() => {entry.request=null;});
  return entry.request;
}
export async function setContactInterest(user, customerId, active) {
  const entry = entryFor(user);
  if (!entry.state.ready || entry.state.pending.has(customerId)) return;
  update(entry, {pending:new Set([...entry.state.pending,customerId])});
  try {
    const {data} = await api.put(`${interestEndpoint(user)}/${encodeURIComponent(customerId)}`, {active});
    const ids = new Set(entry.state.ids);
    for (const id of data?.related_ids || [customerId]) {active ? ids.add(id) : ids.delete(id);}
    entry.version++; update(entry, {ids});
    window.dispatchEvent(new CustomEvent('salesmachine:interest-changed', {detail:{customerId,active,assignedUserId:data?.assigned_user_id}}));
  } finally {
    const pending = new Set(entry.state.pending); pending.delete(customerId); update(entry, {pending});
  }
}
export function useContactInterests(user) {
  const entry = entryFor(user);
  const subscribe = useCallback(listener => {entry.listeners.add(listener);return () => entry.listeners.delete(listener);}, [entry]);
  const snapshot = useCallback(() => entry.state, [entry]);
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);
  // Reuse fresh marker IDs across mounted cards; refresh aliases after the short TTL.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {loadInterests(user);}, [entry]);
  return state;
}
export function resetInterestsForTests() { stores.clear(); }


